import { createClient } from '@supabase/supabase-js'

// These will be replaced with actual credentials when connecting to Supabase
const SUPABASE_URL = 'https://example-project-id.supabase.co'
const SUPABASE_ANON_KEY = 'example-anon-key'

// For now, we will use mock data until Supabase is connected
const isMockMode = true

let supabase = null
if (!isMockMode) {
  supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: { persistSession: true, autoRefreshToken: true }
  })
}

// Create a stable user ID from email
const createStableUserId = (email) => {
  return email.toLowerCase().replace(/[^a-zA-Z0-9]/g, '_')
}

// Get current user from localStorage
const getCurrentUser = () => {
  try {
    const userData = localStorage.getItem('dreamforge_user')
    return userData ? JSON.parse(userData) : null
  } catch (error) {
    console.error('Error getting current user:', error)
    return null
  }
}

// Create user-specific key for data storage
const getUserDataKey = (dataType) => {
  const user = getCurrentUser()
  if (!user || !user.email) {
    return `dreamforge_${dataType}_guest`
  }
  const stableId = createStableUserId(user.email)
  return `dreamforge_${dataType}_${stableId}`
}

// Safe localStorage operations
const safeGetFromStorage = (key, defaultValue = []) => {
  try {
    const stored = localStorage.getItem(key)
    if (!stored) return defaultValue
    const parsed = JSON.parse(stored)
    return Array.isArray(parsed) ? parsed : defaultValue
  } catch (error) {
    console.error(`Error reading from storage for key ${key}:`, error)
    return defaultValue
  }
}

const safeSetToStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data))
    return true
  } catch (error) {
    console.error(`Error saving to storage for key ${key}:`, error)
    return false
  }
}

// Mock data functions with improved persistence
const getMockHabits = () => {
  const key = getUserDataKey('habits')
  const stored = safeGetFromStorage(key, [])

  // If user already has stored data, return it
  if (stored.length > 0) {
    console.log(`Loading ${stored.length} habits for user`)
    return stored
  }

  // Check if user is new and wants example data
  const user = getCurrentUser()
  if (user && user.isNewUser) {
    const defaultHabits = [
      {
        id: 'habit_1',
        title: 'Morning Meditation',
        description: 'Start each day with 10 minutes of mindfulness',
        category: 'wellness',
        icon: '🧘',
        streak: 0,
        target_days: 30,
        goal_amount: 10,
        goal_unit: 'minutes',
        frequency: 'daily',
        created_at: new Date().toISOString(),
        is_active: true
      },
      {
        id: 'habit_2',
        title: 'Read for 30 minutes',
        description: 'Daily reading habit to expand knowledge',
        category: 'learning',
        icon: '📚',
        streak: 0,
        target_days: 30,
        goal_amount: 30,
        goal_unit: 'minutes',
        frequency: 'daily',
        created_at: new Date().toISOString(),
        is_active: true
      },
      {
        id: 'habit_3',
        title: 'Exercise',
        description: 'Stay active with daily workout',
        category: 'fitness',
        icon: '💪',
        streak: 0,
        target_days: 30,
        goal_amount: 1,
        goal_unit: 'session',
        frequency: 'daily',
        created_at: new Date().toISOString(),
        is_active: true
      }
    ]
    safeSetToStorage(key, defaultHabits)
    console.log('Created default habits for new user')
    return defaultHabits
  }

  // Return empty array for existing users with no habits
  return []
}

const getMockProgress = () => {
  const key = getUserDataKey('progress')
  return safeGetFromStorage(key, [])
}

const getMockReflections = () => {
  const key = getUserDataKey('reflections')
  return safeGetFromStorage(key, [])
}

const getMockReminders = () => {
  const key = getUserDataKey('reminders')
  return safeGetFromStorage(key, [])
}

const getMockVisionItems = () => {
  const key = getUserDataKey('visions')
  const stored = safeGetFromStorage(key, [])

  // If user already has stored data, return it
  if (stored.length > 0) {
    console.log(`Loading ${stored.length} visions for user`)
    return stored
  }

  // Check if user is new and wants example data
  const user = getCurrentUser()
  if (user && user.isNewUser) {
    const defaultVisions = [
      {
        id: 'vision_1',
        type: 'goal',
        title: 'Complete a Marathon',
        content: 'Train and complete a full marathon within the next 6 months to challenge myself physically',
        category: 'health',
        linked_habits: ['habit_1', 'habit_3'],
        is_achieved: false,
        target_date: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString(),
        purpose: 'To challenge myself physically and prove I can achieve difficult goals with discipline and persistence',
        created_at: new Date().toISOString(),
        importance: 5,
        timeframe: 'medium',
        progress: 0,
        action_steps: [],
        predicted_actions: [
          { id: 101, text: 'Complete half-marathon training program', completed: false },
          { id: 102, text: 'Gradually increase weekly mileage', completed: false },
          { id: 103, text: 'Research and sign up for a marathon event', completed: false }
        ],
        predicted_obstacles: [
          { id: 201, text: 'Potential injury from overtraining', mitigated: false },
          { id: 202, text: 'Time management challenges', mitigated: false },
          { id: 203, text: 'Weather conditions affecting training', mitigated: false }
        ],
        failures: [],
        adjustments: [],
        journey_entries: [],
        milestones: []
      }
    ]
    safeSetToStorage(key, defaultVisions)
    console.log('Created default visions for new user')
    return defaultVisions
  }

  return []
}

// Helper function to generate unique IDs
const generateUniqueId = () => {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// Helper function to save data to localStorage with error handling
const saveToStorage = (dataType, data) => {
  const key = getUserDataKey(dataType)
  const success = safeSetToStorage(key, data)
  if (success) {
    console.log(`Successfully saved ${data.length} ${dataType} items`)
  }
  return success
}

// Mock API functions with improved data handling
export const habitAPI = {
  async getHabits() {
    if (isMockMode) {
      const habits = getMockHabits()
      console.log('habitAPI.getHabits() - returning:', habits.length, 'habits')
      return { data: habits, error: null }
    }
    return await supabase.from('habits').select('*').eq('is_active', true)
  },

  async createHabit(habit) {
    if (isMockMode) {
      const habits = getMockHabits()
      const newHabit = {
        ...habit,
        id: generateUniqueId(),
        created_at: new Date().toISOString(),
        streak: 0,
        is_active: true
      }
      habits.push(newHabit)
      const success = saveToStorage('habits', habits)
      if (success) {
        console.log('Created new habit:', newHabit.title)
        return { data: newHabit, error: null }
      } else {
        return { data: null, error: 'Failed to save habit' }
      }
    }
    return await supabase.from('habits').insert([habit]).select()
  },

  async updateHabit(id, updates) {
    if (isMockMode) {
      const habits = getMockHabits()
      const habitIndex = habits.findIndex(h => h.id === id || h.id == id)
      if (habitIndex !== -1) {
        habits[habitIndex] = { ...habits[habitIndex], ...updates }
        const success = saveToStorage('habits', habits)
        if (success) {
          console.log('Updated habit:', habits[habitIndex].title)
          return { data: habits[habitIndex], error: null }
        }
      }
      return { data: null, error: 'Habit not found or save failed' }
    }
    return await supabase.from('habits').update(updates).eq('id', id).select()
  },

  async deleteHabit(id) {
    if (isMockMode) {
      const habits = getMockHabits()
      const habitIndex = habits.findIndex(h => h.id === id || h.id == id)
      if (habitIndex !== -1) {
        const deletedHabit = habits[habitIndex]
        habits.splice(habitIndex, 1)
        const success = saveToStorage('habits', habits)
        if (success) {
          console.log('Deleted habit:', deletedHabit.title)
          return { data: null, error: null }
        }
      }
      return { data: null, error: 'Habit not found or delete failed' }
    }
    return await supabase.from('habits').delete().eq('id', id)
  },

  async getProgress(habitId, startDate, endDate) {
    if (isMockMode) {
      const progress = getMockProgress()
      const filtered = progress.filter(p => 
        (p.habit_id === habitId || p.habit_id == habitId) && 
        p.date >= startDate && 
        p.date <= endDate
      )
      return { data: filtered, error: null }
    }
    return await supabase
      .from('habit_progress')
      .select('*')
      .eq('habit_id', habitId)
      .gte('date', startDate)
      .lte('date', endDate)
  },

  async markComplete(habitId, date, completed = true) {
    if (isMockMode) {
      const progress = getMockProgress()
      const existingIndex = progress.findIndex(p => 
        (p.habit_id === habitId || p.habit_id == habitId) && p.date === date
      )

      if (existingIndex !== -1) {
        progress[existingIndex].completed = completed
      } else {
        progress.push({
          id: generateUniqueId(),
          habit_id: habitId,
          date,
          completed,
          created_at: new Date().toISOString()
        })
      }

      // Update habit streak when marking complete
      if (completed) {
        const habits = getMockHabits()
        const habitIndex = habits.findIndex(h => h.id === habitId || h.id == habitId)
        if (habitIndex !== -1) {
          // Simple streak increment - in a real app this would calculate based on consecutive days
          habits[habitIndex].streak = (habits[habitIndex].streak || 0) + 1
          saveToStorage('habits', habits)
        }
      }

      const success = saveToStorage('progress', progress)
      if (success) {
        console.log(`Marked habit ${habitId} as ${completed ? 'complete' : 'incomplete'} for ${date}`)
        return { data: { habit_id: habitId, date, completed }, error: null }
      } else {
        return { data: null, error: 'Failed to save progress' }
      }
    }
    return await supabase
      .from('habit_progress')
      .upsert({ habit_id: habitId, date, completed })
      .select()
  }
}

export const reflectionAPI = {
  async getReflections() {
    if (isMockMode) {
      const reflections = getMockReflections()
      return { data: reflections, error: null }
    }
    return await supabase.from('reflections').select('*').order('date', { ascending: false })
  },

  async createReflection(reflection) {
    if (isMockMode) {
      const reflections = getMockReflections()
      const newReflection = {
        ...reflection,
        id: generateUniqueId(),
        created_at: new Date().toISOString()
      }
      reflections.unshift(newReflection)
      const success = saveToStorage('reflections', reflections)
      if (success) {
        console.log('Created new reflection:', newReflection.title)
        return { data: newReflection, error: null }
      } else {
        return { data: null, error: 'Failed to save reflection' }
      }
    }
    return await supabase.from('reflections').insert([reflection]).select()
  },

  async updateReflection(id, updates) {
    if (isMockMode) {
      const reflections = getMockReflections()
      const reflectionIndex = reflections.findIndex(r => r.id === id || r.id == id)
      if (reflectionIndex !== -1) {
        reflections[reflectionIndex] = { ...reflections[reflectionIndex], ...updates }
        const success = saveToStorage('reflections', reflections)
        if (success) {
          return { data: reflections[reflectionIndex], error: null }
        }
      }
      return { data: null, error: 'Reflection not found or save failed' }
    }
    return await supabase.from('reflections').update(updates).eq('id', id).select()
  },

  async deleteReflection(id) {
    if (isMockMode) {
      const reflections = getMockReflections()
      const reflectionIndex = reflections.findIndex(r => r.id === id || r.id == id)
      if (reflectionIndex !== -1) {
        reflections.splice(reflectionIndex, 1)
        const success = saveToStorage('reflections', reflections)
        if (success) {
          return { data: null, error: null }
        }
      }
      return { data: null, error: 'Reflection not found or delete failed' }
    }
    return await supabase.from('reflections').delete().eq('id', id)
  }
}

export const reminderAPI = {
  async getReminders() {
    if (isMockMode) {
      const reminders = getMockReminders()
      return { data: reminders, error: null }
    }
    return await supabase.from('reminders').select('*').order('time', { ascending: true })
  },

  async createReminder(reminder) {
    if (isMockMode) {
      const reminders = getMockReminders()
      const newReminder = {
        ...reminder,
        id: generateUniqueId(),
        created_at: new Date().toISOString()
      }
      reminders.push(newReminder)
      const success = saveToStorage('reminders', reminders)
      if (success) {
        console.log('Created new reminder:', newReminder.title)
        return { data: newReminder, error: null }
      } else {
        return { data: null, error: 'Failed to save reminder' }
      }
    }
    return await supabase.from('reminders').insert([reminder]).select()
  },

  async updateReminder(id, updates) {
    if (isMockMode) {
      const reminders = getMockReminders()
      const reminderIndex = reminders.findIndex(r => r.id === id || r.id == id)
      if (reminderIndex !== -1) {
        reminders[reminderIndex] = { ...reminders[reminderIndex], ...updates }
        const success = saveToStorage('reminders', reminders)
        if (success) {
          return { data: reminders[reminderIndex], error: null }
        }
      }
      return { data: null, error: 'Reminder not found or save failed' }
    }
    return await supabase.from('reminders').update(updates).eq('id', id).select()
  },

  async deleteReminder(id) {
    if (isMockMode) {
      const reminders = getMockReminders()
      const reminderIndex = reminders.findIndex(r => r.id === id || r.id == id)
      if (reminderIndex !== -1) {
        reminders.splice(reminderIndex, 1)
        const success = saveToStorage('reminders', reminders)
        if (success) {
          return { data: null, error: null }
        }
      }
      return { data: null, error: 'Reminder not found or delete failed' }
    }
    return await supabase.from('reminders').delete().eq('id', id)
  }
}

export const visionBoardAPI = {
  async getVisionItems() {
    if (isMockMode) {
      const visions = getMockVisionItems()
      console.log('visionBoardAPI.getVisionItems() - returning:', visions.length, 'visions')
      return { data: visions, error: null }
    }
    return await supabase.from('vision_items').select('*').order('created_at', { ascending: false })
  },

  async createVisionItem(item) {
    if (isMockMode) {
      const visions = getMockVisionItems()
      const newItem = {
        ...item,
        id: generateUniqueId(),
        created_at: new Date().toISOString()
      }
      visions.unshift(newItem)
      const success = saveToStorage('visions', visions)
      if (success) {
        console.log('Created new vision:', newItem.title)
        return { data: newItem, error: null }
      } else {
        return { data: null, error: 'Failed to save vision' }
      }
    }
    return await supabase.from('vision_items').insert([item]).select()
  },

  async updateVisionItem(id, updates) {
    if (isMockMode) {
      const visions = getMockVisionItems()
      const itemIndex = visions.findIndex(item => item.id === id || item.id == id)
      if (itemIndex !== -1) {
        visions[itemIndex] = { ...visions[itemIndex], ...updates }
        const success = saveToStorage('visions', visions)
        if (success) {
          console.log('Updated vision:', visions[itemIndex].title)
          return { data: visions[itemIndex], error: null }
        }
      }
      return { data: null, error: 'Vision item not found or save failed' }
    }
    return await supabase.from('vision_items').update(updates).eq('id', id).select()
  },

  async deleteVisionItem(id) {
    if (isMockMode) {
      const visions = getMockVisionItems()
      const itemIndex = visions.findIndex(item => item.id === id || item.id == id)
      if (itemIndex !== -1) {
        const deletedVision = visions[itemIndex]
        visions.splice(itemIndex, 1)
        const success = saveToStorage('visions', visions)
        if (success) {
          console.log('Deleted vision:', deletedVision.title)
          return { data: null, error: null }
        }
      }
      return { data: null, error: 'Vision item not found or delete failed' }
    }
    return await supabase.from('vision_items').delete().eq('id', id)
  }
}

export default supabase